import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { linkedInPosts } from '@/mocks/linkedinFeed';

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

const AUTO_SCROLL_DELAY = 3500;

function CompactPostCard({ post, index }: { post: (typeof linkedInPosts)[0]; index: number }) {
  const [liked, setLiked] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4, ease: 'easeOut' }}
      className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden flex-shrink-0 w-[300px] sm:w-[340px] snap-start select-none"
    >
      {/* Post header */}
      <div className="p-3.5 flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-navy-900 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">T</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-navy-900 text-xs">Thoughtic Solutions</span>
            <span className="text-gray-400 text-[10px]">·</span>
            <span className="text-gray-500 text-[10px]">{post.date}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">Engineering Talent Partner · 12.4k followers</div>
        </div>
      </div>

      {/* Content */}
      <div className="px-3.5 pb-2">
        <p className="text-xs text-gray-700 leading-relaxed line-clamp-4">
          {post.content}
</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {post.hashtags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 hover:underline cursor-pointer transition-colors">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Image */}
      {post.image && (
        <div className="px-3.5 pb-2">
          <div className="rounded-lg overflow-hidden border border-gray-100 h-28">
            <img src={post.image} alt="Post attachment" className="w-full h-full object-cover" draggable={false} />
          </div>
        </div>
      )}

      {/* Engagement bar */}
      <div className="px-3.5 py-2.5 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLiked(!liked)}
            className={`flex items-center gap-1 text-[10px] font-medium transition-colors cursor-pointer ${liked ? 'text-rose-500' : 'text-gray-500 hover:text-rose-500'}`}
          >
            <i className={`${liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm w-4 h-4 flex items-center justify-center`}></i>
            <span>{formatNumber(liked ? post.likes + 1 : post.likes)}</span>
          </button>
          <button className="flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-primary-600 transition-colors cursor-pointer">
            <i className="ri-message-3-line text-sm w-4 h-4 flex items-center justify-center"></i>
            <span>{formatNumber(post.comments)}</span>
          </button>
        </div>
        <a
          href="https://linkedin.com/company/thoughtic-solutions"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="text-[10px] font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          View on LinkedIn
        </a>
      </div>
    </motion.div>
  );
}

export default function LinkedInFeed() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const autoScrollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const TOTAL_CARDS = linkedInPosts.length;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -350 : 350, behavior: 'smooth' });
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
      autoScrollTimerRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollTimerRef.current) clearInterval(autoScrollTimerRef.current);
    autoScrollTimerRef.current = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 20) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: 340, behavior: 'smooth' });
      }
    }, AUTO_SCROLL_DELAY);
  }, []);

  useEffect(() => {
    if (!inView || isHovered) {
      stopAutoScroll();
      return;
    }
    startAutoScroll();
    return () => stopAutoScroll();
  }, [inView, isHovered, startAutoScroll, stopAutoScroll]);

  useEffect(() => () => stopAutoScroll(), [stopAutoScroll]);

  return (
    <section className="py-10 md:py-14 bg-stone-50/60 relative overflow-hidden" ref={ref}>
      <div className="relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-6 md:mb-8 px-4 sm:px-6 lg:px-8"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full mb-3 shadow-sm"
          >
            <i className="ri-linkedin-box-fill text-primary-600 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-xs font-medium text-gray-700">From LinkedIn</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6 mb-3"
          >
            Follow Our
            <span className="text-primary-600"> Journey</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-sm text-gray-500 max-w-lg mx-auto mb-4"
          >
            Engineering wins, team milestones, and industry insights — shared weekly.
          </motion.p>

          <motion.a
            href="https://linkedin.com/company/thoughtic-solutions"
            target="_blank"
            rel="nofollow noopener noreferrer"
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-navy-900 text-white rounded-lg font-semibold text-xs hover:bg-navy-800 transition-colors cursor-pointer"
          >
            <i className="ri-linkedin-box-fill text-sm w-4 h-4 flex items-center justify-center"></i>
            Follow Us on LinkedIn
            <i className="ri-external-link-line text-xs w-4 h-4 flex items-center justify-center opacity-70"></i>
          </motion.a>
        </motion.div>

        {/* Compact carousel */}
        <div
          className="relative group"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Left arrow */}
          <button
            onClick={() => scrollBy('left')}
            className={`absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-all duration-300 cursor-pointer shadow-sm ${
              canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <i className="ri-arrow-left-s-line text-lg w-6 h-6 flex items-center justify-center"></i>
          </button>

          {/* Right arrow */}
          <button
            onClick={() => scrollBy('right')}
            className={`absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-all duration-300 cursor-pointer shadow-sm ${
              canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <i className="ri-arrow-right-s-line text-lg w-6 h-6 flex items-center justify-center"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 px-4 sm:px-8 lg:px-12"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex-shrink-0 w-0 sm:w-2 lg:w-4 snap-none" aria-hidden="true"></div>

            {linkedInPosts.map((post, i) => (
              <CompactPostCard key={post.id} post={post} index={i} />
            ))}

            <div className="flex-shrink-0 w-0 sm:w-2 lg:w-4 snap-none" aria-hidden="true"></div>
          </div>

          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 px-2.5 py-0.5 bg-navy-900/70 text-white text-[10px] font-medium rounded-full backdrop-blur-sm pointer-events-none"
            >
              <i className="ri-pause-circle-line w-3 h-3 flex items-center justify-center inline mr-1"></i>
              Paused
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}